import React, { useRef } from "react";
import styles from "../CodeValidation/CodeValidation.module.scss";
import Button from "../Forms/Button/Button.js";
import { motion } from "framer-motion";
import { X, RefreshCw } from "lucide-react";
import { useCodeValidation } from "../../../hooks/registration/useCodeValidation.ts";
import { useRegistrationFlow } from "../../../hooks/registration/useRegistrationFlow.ts";

interface CodeValidationV2Props {
  email: string;
  specialMessage?: string;
}

const CodeValidationV2: React.FC<CodeValidationV2Props> = ({
  email,
  specialMessage,
}) => {
  const registrationFlow = useRegistrationFlow();
  const {
    code,
    setCode,
    codeError,
    setCodeError,
    countDown,
    showResend,
    handleResend,
    handleChangeEmail,
    handlePaste,
    isLoading,
  } = useCodeValidation({
    email,
    validateVerificationCode: registrationFlow.validateVerificationCode,
    resendVerificationCode: registrationFlow.resendVerificationCode,
    setStep: registrationFlow.setStep,
    codeValidationLoading: registrationFlow.codeValidation.isLoading,
    resendCodeLoading: registrationFlow.resendCode.isLoading,
    codeValidationError: registrationFlow.codeValidation.error,
    resendCodeError: registrationFlow.resendCode.error,
  });

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [showSpecialToast, setShowSpecialToast] = React.useState<boolean>(true);

  const handleCodeChange = (index: number, value: string): void => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = code.substring(0, index) + value + code.substring(index + 1);
    setCode(newCode);
    
    if (codeError && value) {
      setCodeError('');
    }
    
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCloseSpecialToast = (): void => {
    setShowSpecialToast(false);
  };

  const containerVariant = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariant = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  return (
    <motion.div
      variants={containerVariant}
      initial="hidden"
      animate="visible"
      className={styles.container}
    >
      <motion.div variants={itemVariant} className={styles.content}>
        <motion.p variants={itemVariant} className={styles.subtitle}>
          Código enviado a <strong>{email}</strong>
        </motion.p>
        
        {specialMessage && showSpecialToast && (
          <motion.div 
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className={styles.specialToast}
          >
            <div className={styles.specialToast_content}>
              <span className={styles.specialToast_text}>{specialMessage}</span>
              <button 
                onClick={handleCloseSpecialToast}
                className={styles.specialToast_close}
                type="button"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}

        <motion.div variants={itemVariant} className={styles.codeContainer}>
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={code[index] || ""}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={styles.codeInput}
              disabled={isLoading}
              autoComplete="one-time-code"
              placeholder="-"
            />
          ))}
        </motion.div>

        {codeError && (
          <motion.p 
            variants={itemVariant}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className={styles.errorMessage}
          >
            {codeError}
          </motion.p>
        )}

        <motion.div variants={itemVariant} className={styles.actions}>
          {showResend ? (
            <Button
              onClick={handleResend}
              disabled={isLoading}
              className={styles.resendButton}
              colored
              bordered={false}
              link={false}
              typebtn="primary"
            >
              <RefreshCw size={18} className={styles.resendIcon} />
              {isLoading ? "Reenviando..." : "Reenviar código"}
            </Button>
          ) : (
            <p className={styles.countdown}>
              <RefreshCw size={18} className={styles.resendIcon} />
              Reenviar en {countDown}s
            </p>
          )}
        </motion.div>

        {codeError && (
          <motion.div variants={itemVariant} className={styles.help}>
            <p>¿No recibiste el código?</p>
            <ul>
              <li>Revisa tu carpeta de spam</li>
              <li>Verifica que el email sea correcto o <button onClick={handleChangeEmail} className={styles.helpLink} type="button">cambia el email</button></li>
            </ul>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default CodeValidationV2;
